import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './ContextMenu.module.scss';

type ContextMenuItem = {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  icon?: React.ReactNode;
};

type ContextMenuProps = {
  items: ContextMenuItem[];
  isOpen: boolean;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
  align?: 'end' | 'center';
};
type Position = {
  top?: number;
  bottom?: number;
  left: number;
  maxHeight: number;
};

const EDGE_GAP = 8;

const ContextMenu = ({ items, isOpen, onClose, anchorEl, align = 'end' }: ContextMenuProps) => {
  const [position, setPosition] = useState<Position | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Measured off the rendered menu, before it paints. It used to assume the max-width
  // (240px), but the menu sizes to its labels — an Edit / Delete menu is ~150px wide —
  // so an end-aligned menu landed ~90px left of the button that opened it.
  useLayoutEffect(() => {
    if (!isOpen || !anchorEl) return;

    const calculate = () => {
      const menu = menuRef.current;
      if (!menu) return;
      const rect = anchorEl.getBoundingClientRect();
      const MENU_WIDTH = menu.offsetWidth;
      const MENU_HEIGHT = menu.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom - EDGE_GAP * 2;
      const spaceAbove = rect.top - EDGE_GAP * 2;
      // Down unless it does not fit there and there is more room above; when neither
      // side fits, the menu is capped to the roomier one and scrolls inside.
      const openUpward = spaceBelow < MENU_HEIGHT && spaceAbove > spaceBelow;
      const maxHeight = Math.max(80, openUpward ? spaceAbove : spaceBelow);
      const left =
        align === 'center'
          ? Math.max(
              EDGE_GAP,
              Math.min(
                rect.left + rect.width / 2 - MENU_WIDTH / 2,
                window.innerWidth - MENU_WIDTH - EDGE_GAP,
              ),
            )
          : Math.max(
              EDGE_GAP,
              Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - EDGE_GAP),
            );

      setPosition(
        openUpward
          ? { bottom: window.innerHeight - rect.top + EDGE_GAP, top: undefined, left, maxHeight }
          : { top: rect.bottom + EDGE_GAP, bottom: undefined, left, maxHeight },
      );
    };

    calculate();
    window.addEventListener('resize', calculate);
    return () => {
      window.removeEventListener('resize', calculate);
      // The next open measures from the unplaced state again: a stale `left` near the
      // viewport edge would squeeze the menu to its min-width while it is measured.
      setPosition(null);
    };
  }, [isOpen, anchorEl, items.length, align]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Scrolling the list itself (a long one) must not close it; any other scroll
    // moves the anchor away, so it does.
    const handleScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      onClose();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, onClose, anchorEl]);

  // Portaled out of the opener's tab order (and out of a Modal's focus trap): once
  // placed, the menu takes focus and handles its own keys.
  const placed = position !== null;
  useEffect(() => {
    if (!placed) return;
    menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
  }, [placed]);

  if (!isOpen) return null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [],
    );
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const focusAt = (i: number) => buttons[(i + buttons.length) % buttons.length]?.focus();
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusAt(index + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusAt(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusAt(0);
        break;
      case 'End':
        e.preventDefault();
        focusAt(buttons.length - 1);
        break;
      case 'Escape':
      case 'Tab':
        // A Modal listens for Escape on document and would close itself.
        e.preventDefault();
        e.stopPropagation();
        onClose();
        anchorEl?.focus();
        break;
    }
  };

  const menu = (
    <div
      ref={menuRef}
      role="menu"
      onKeyDown={handleKeyDown}
      className={styles.contextMenu}
      // Mounted hidden at the origin until placed, so it can be measured first.
      style={
        position
          ? {
              position: 'fixed',
              top: position.top !== undefined ? `${position.top}px` : undefined,
              bottom: position.bottom !== undefined ? `${position.bottom}px` : undefined,
              left: `${position.left}px`,
              maxHeight: `${position.maxHeight}px`,
            }
          : { position: 'fixed', top: 0, left: 0, visibility: 'hidden' }
      }
    >
      {items.map((item, index) => (
        <button
          key={index}
          type="button"
          role="menuitem"
          className={item.variant === 'danger' ? styles.menuItemDanger : styles.menuItem}
          onClick={(e) => {
            e.stopPropagation();
            item.onClick();
            onClose();
          }}
        >
          {item.icon && item.icon}
          <span className={styles.menuItemLabel}>{item.label}</span>
        </button>
      ))}
    </div>
  );

  return createPortal(menu, document.body);
};

export default ContextMenu;
